import HighlightableScrollRef from "@cmp/highlightableScrollRef"
import {
  CourseLanguage,
  DatacampLesson,
  Topic,
} from "@masterschool/course-builder-api"
import { Box, Button, SvgIcon } from "@mui/material"
import appIcons from "@utils/appIcons"
import SyllabusElementFactory from "@utils/syllabusElementFactory"
import { useEffect, useRef, useState } from "react"
import appTheme from "../../../theme/appTheme"
import TimeEstimationTextField from "../../components/timeEstimationTextField"
import { useEditDatacampLesson } from "../custom-hooks/useEditElementItem"
import { useSelectedDatacampLesson } from "../custom-hooks/useSelectedElementItem"
import EditLessonType from "./EditLessonType"
import DatacampGroupsEditor from "./task-editors/datacampGroupsEditor"
import { useAppSelector } from "@app/hooks"
import { MultiLanguageTitleTextField } from "../../components/multiLanguageFields/multiLanguageTitleTextField"

function DatacampLessonEditor(props: { topic: Topic }) {
  const { topic } = props
  const lesson = useSelectedDatacampLesson()
  const editLesson = useEditDatacampLesson()
  const editingLanguage = useAppSelector(
    (state) => state.courseEditor.editingLanguage,
  )

  if (!lesson) {
    return undefined
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        gap: "24px",
        padding: "24px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          gap: "16px",
        }}
      >
        <MultiLanguageTitleTextField
          elementId={lesson.id}
          language={editingLanguage}
          defaultLanguageValue={lesson.title}
          extraLanguageValueRecord={lesson.extraLanguageTitleRecord}
          debounce
          onChangeDefaultLanguage={(value) => {
            editLesson(lesson, "title", value, topic)
          }}
          onChangeExtraLanguage={(value) => {
            editLesson(lesson, "extraLanguageTitleRecord", value, topic)
          }}
        />
        <EditLessonType topic={topic} />
      </Box>
      <TimeEstimationTextField
        elementId={lesson.id}
        value={lesson.estimatedDuration}
        debounce
        onChange={(e) => {
          editLesson(
            lesson,
            "estimatedDuration",
            parseInt(e.target.value),
            topic,
          )
        }}
      />
      <TasksEditorContainer
        lesson={lesson}
        topic={topic}
        editingLanguage={editingLanguage}
      />
    </Box>
  )
}

const TasksEditorContainer = (props: {
  lesson: DatacampLesson
  topic: Topic
  editingLanguage: CourseLanguage
}) => {
  const { lesson, topic, editingLanguage } = props
  const editLesson = useEditDatacampLesson()
  const tasksEditorRef = useRef<HighlightableScrollRef>(null)
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false)

  useEffect(() => {
    if (shouldScrollToBottom) {
      tasksEditorRef.current?.scrollToBottomAndHighlightLastTask()
    }
    setShouldScrollToBottom(false)
  }, [shouldScrollToBottom])

  const addGroupText =
    lesson.tasks.length === 0 ? "Add group (optional)" : "Add group"

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "start",
        gap: "16px",
        overflow: "hidden",
      }}
    >
      <DatacampGroupsEditor
        ref={tasksEditorRef}
        topic={topic}
        editingLanguage={editingLanguage}
      />
      <Button
        variant="text"
        size="small"
        onClick={() => {
          editLesson(
            lesson,
            "tasks",
            lesson.tasks.concat(
              SyllabusElementFactory.makeDatacampTasksGroup(),
            ),
            topic,
          )
          setShouldScrollToBottom(true)
        }}
        startIcon={
          <SvgIcon
            component={appIcons.plusCircle}
            inheritViewBox
            sx={{
              width: "20px",
              height: "20px",
              fill: "none",
              stroke: appTheme.palette.icon.black,
            }}
          />
        }
      >
        {addGroupText}
      </Button>
    </Box>
  )
}

export default DatacampLessonEditor
